import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import api, { apiErr, fmtDate, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ClipboardList, Pencil, Trash2, Send, Check, Eye, Loader2, FileDown, CalendarRange } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["activites", "Activités réalisées"], ["taches", "Tâches effectuées"], ["resultats", "Résultats obtenus"],
  ["difficultes", "Difficultés rencontrées"], ["points_suivi", "Points nécessitant un suivi"],
  ["taches_demain", "Tâches prévues pour demain"], ["commentaires", "Commentaires"],
];

export default function DailyReports() {
  const { role } = useAuth();
  const [items, setItems] = useState(null);
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [fStatut, setFStatut] = useState("all");
  const [q, setQ] = useState("");

  const load = () => { api.get("/daily-reports").then((r) => setItems(r.data)); api.get("/daily-reports/dashboard").then((r) => setDash(r.data)).catch(() => {}); };
  useEffect(() => { load(); api.get("/settings").then((r) => setTemplates(r.data.report_templates || [])).catch(() => {}); }, []);
  const isManager = dash?.is_manager;
  const applyTemplate = (label) => {
    const t = templates.find((x) => x.label === label);
    if (!t) return;
    setForm((p) => ({ ...p, activites: t.activites || "", taches: t.taches || "", resultats: t.resultats || "",
      difficultes: t.difficultes || "", points_suivi: t.points_suivi || "", taches_demain: t.taches_demain || "",
      commentaires: t.commentaires || "" }));
    toast.success(`Modèle « ${t.label} » appliqué`);
  };

  const empty = () => ({ date: new Date().toISOString().slice(0, 10), activites: "", taches: "", resultats: "", difficultes: "", points_suivi: "", taches_demain: "", commentaires: "" });
  const openNew = () => { setForm(empty()); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setOpen(true); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (statut) => {
    setSaving(true);
    try {
      const payload = { ...form, statut };
      if (form.id) await api.put(`/daily-reports/${form.id}`, payload); else await api.post("/daily-reports", payload);
      toast.success(statut === "Soumis" ? "Rapport soumis" : "Brouillon enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const validate = async (id) => { try { await api.post(`/daily-reports/${id}/validate`); toast.success("Rapport validé"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (id) => { if (!window.confirm("Supprimer ce rapport ?")) return; await api.delete(`/daily-reports/${id}`); toast.success("Supprimé"); load(); };
  const openPdf = (url, name) => { const t = localStorage.getItem("omnibat_token"); fetch(`${API_BASE}${url}`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => { if (!r.ok) throw new Error("PDF indisponible"); return r.blob(); }).then((b) => window.open(URL.createObjectURL(b))).catch(() => toast.error("Impossible de générer le PDF")); };
  const downloadPdf = (id) => openPdf(`/daily-reports/${id}/pdf`);
  const weeklyPdf = () => openPdf(`/daily-reports/weekly-pdf`);

  const list = (items || []).filter((r) => (fStatut === "all" || r.statut === fStatut) && (!q || `${r.user_name} ${r.departement} ${r.activites}`.toLowerCase().includes(q.toLowerCase())));

  const KPI = ({ label, value, accent }) => (
    <div className={`rounded-xl border p-4 ${accent || "bg-white"}`}><p className="text-[10px] uppercase font-mono text-slate-400">{label}</p><p className="font-head text-2xl font-bold mt-1">{value}</p></div>
  );

  return (
    <div>
      <PageHeader title="Rapports des collaborateurs" subtitle="Rapport journalier d'activité — brouillon, soumission & validation">
        <Button variant="outline" onClick={weeklyPdf} data-testid="weekly-pdf"><CalendarRange className="h-4 w-4 mr-1.5" />Récap hebdo (PDF)</Button>
        <Button onClick={openNew} data-testid="add-report" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau rapport</Button>
      </PageHeader>

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="reports-dashboard">
          <KPI label="Total" value={dash.total} />
          <KPI label="Du jour" value={dash.du_jour} accent="bg-brand-50 border-brand-200" />
          <KPI label="En attente" value={dash.en_attente} accent={dash.en_attente > 0 ? "bg-amber-50 border-amber-200" : ""} />
          <KPI label="Validés" value={dash.valides} accent="bg-emerald-50 border-emerald-200" />
          <KPI label="Brouillons" value={dash.brouillons} />
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="max-w-xs" data-testid="report-search" />
        <Select value={fStatut} onValueChange={setFStatut}><SelectTrigger className="w-44" data-testid="report-filter-statut"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tous les statuts</SelectItem><SelectItem value="Brouillon">Brouillon</SelectItem><SelectItem value="Soumis">Soumis</SelectItem><SelectItem value="Validé">Validé</SelectItem></SelectContent></Select>
      </div>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Aucun rapport" subtitle="Rédigez votre rapport journalier d'activité." />
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="rounded-xl border bg-white p-4 flex items-center gap-3" data-testid={`report-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-800">{r.user_name}</p>
                  {r.departement && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{r.departement}</span>}
                  <span className="text-xs text-slate-400">{fmtDate(r.date)}</span>
                  <StatusBadge value={r.statut} />
                </div>
                <p className="text-sm text-slate-500 truncate mt-1">{r.activites || "—"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => setView(r)} data-testid={`report-view-${r.id}`}><Eye className="h-4 w-4 text-slate-500" /></Button>
                <Button variant="ghost" size="icon" onClick={() => downloadPdf(r.id)} data-testid={`report-pdf-${r.id}`}><FileDown className="h-4 w-4 text-slate-500" /></Button>
                {isManager && r.statut === "Soumis" && <Button size="sm" onClick={() => validate(r.id)} className="bg-emerald-600 text-white h-8" data-testid={`report-validate-${r.id}`}><Check className="h-4 w-4 mr-1" />Valider</Button>}
                {r.statut !== "Validé" && <Button variant="ghost" size="icon" onClick={() => openEdit(r)}><Pencil className="h-4 w-4 text-slate-400" /></Button>}
                <Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-slate-300 hover:text-rose-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier le rapport" : "Nouveau rapport journalier"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1 max-w-xs" data-testid="report-date" /></div>
              {templates.length > 0 && (
                <div className="rounded-lg border bg-brand-50/40 p-3">
                  <Label className="text-xs font-semibold text-brand-700">Modèle de rapport (pré-remplir selon le métier)</Label>
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="mt-1.5 max-w-sm bg-white" data-testid="report-template"><SelectValue placeholder="Choisir un modèle…" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.label} value={t.label}>{t.label}{t.fonction ? ` — ${t.fonction}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {FIELDS.map(([k, label]) => (
                <div key={k}><Label className="text-xs">{label}</Label><div className="mt-1"><RichTextEditor value={form[k] || ""} onChange={(v) => set(k, v)} testId={`report-${k}`} rows={k === "activites" ? 3 : 2} placeholder={label} /></div></div>
              ))}
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="button" variant="outline" disabled={saving} onClick={() => save("Brouillon")} data-testid="report-draft">Enregistrer le brouillon</Button>
                <Button type="button" disabled={saving} onClick={() => save("Soumis")} data-testid="report-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" />Soumettre</>}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Viewer */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {view && (<>
            <DialogHeader><DialogTitle className="font-head">Rapport de {view.user_name} — {fmtDate(view.date)}</DialogTitle></DialogHeader>
            <div className="flex items-center gap-2 flex-wrap"><StatusBadge value={view.statut} />{view.departement && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{view.departement}</span>}{view.fonction && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{view.fonction}</span>}{view.validated_by && <span className="text-xs text-emerald-600">Validé par {view.validated_by}</span>}</div>
            <div className="space-y-3 mt-2">
              {FIELDS.map(([k, label]) => view[k] ? (
                <div key={k}><p className="text-[11px] uppercase font-mono text-slate-400">{label}</p><div className="text-sm text-slate-700 rte-content [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(view[k]) }} /></div>
              ) : null)}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
